import { Route } from 'vue-router';

import { DocumentValueCodes } from '@/enums/documentValueCodes.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Storage } from '@/enums/storage.enum';
import store from '@/store/store';
import { flattenNestedQueries } from './document';
import { isNullable } from './isNullable';
import { tryParseInt } from './number';
import { parseJsonOrDefault } from './parseJsonOrDefault';
import { prepareQuery } from './prepareQuery';

/**
 * Pobiera statystyki
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getStatistics(route: Route): Promise<any> {
  return store.dispatch('statistics/getProcedureStatistics', {
    procedureId: tryParseInt(route.params.procedureId),
    filters: { filterFields: getReportFilters(route) }
  });
}

/**
 * Pobiera raport finansowy
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getFinancialReport(route: Route): Promise<any> {
  return store.dispatch('documentIndex/getFinancialReport', {
    procedureId: tryParseInt(route.params.procedureId),
    structureIds: store.state.auth.nodes?.map((node: any) => node.id)
  });
}

/**
 * Pobiera ewidencję
 *
 * @export
 * @param {Route} route
 * @returns
 */
export function getReportList(route: Route) {
  const filterFields = getReportFilters(route);
  const unwinds = getReportUnwinds(route);

  return store.dispatch('documentIndex/getStatistics', {
    filters: { ...prepareQuery(route), count: -1, filterFields },
    joinChildren: !!store.state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value,
    unwinds
  });
}

/**
 * Pobiera raporty
 *
 * @export
 * @param {Route} route
 * @param {*} sort
 */
export function getReportGroups(route: Route) {
  const procedureId = route.params.procedureId.toString();
  const storageColumns = sessionStorage.getItem(Storage.REPORT_GROUPS + procedureId);
  const selectedColumns: any[] = parseJsonOrDefault(storageColumns, []);
  const filterFields = getReportFilters(route);
  const order = route?.query?.order as string;

  const nameFields = selectedColumns.map(column => column.name);
  const sortByFields: string[] = [];
  selectedColumns.forEach(column => {
    if (column.sortBy) {
      sortByFields.push(column.sortBy);
    }
  });

  return store.dispatch('documentIndex/getStatistics', {
    groups: nameFields.concat(sortByFields),
    filters: {
      filterFields
    },
    sort: order
      ? {
          order: order === 'count' ? order : '_id.' + order.replace(/\./g, '__'),
          desc: route.query.desc
        }
      : { order: 'count', desc: false }
  });
}

/**
 * Zwraca listę wybranych filtrów
 *
 * @returns {*}
 */
export function getReportFilters(route: Route): any {
  const procedureId = route.params.procedureId.toString();
  const templateId = store.state.procedure.data.templateId;
  const storageFiltersJSON = sessionStorage.getItem(Storage.REPORT_FILTERS + procedureId);
  const storageFilters: any[] = parseJsonOrDefault(storageFiltersJSON, []);

  const filters = storageFilters
    .filter(filter => filter.value && filter.value !== 'null')
    .map(filter => {
      if (Array.isArray(filter.value)) {
        const values = filter.value.map((value: any) =>
          filter.isJson ? { [filter.name]: parseJsonOrDefault(value) } : { [filter.name]: value }
        );
        return { $or: values };
      } else {
        return { [filter.name]: filter.isJson ? parseJsonOrDefault(filter.value) : filter.value };
      }
    });

  filters.forEach(filter => flattenNestedQueries(filter));

  return {
    'procedure.templateId': tryParseInt(templateId),
    $and: [
      {
        [`values.${DocumentValueCodes.SUBMIT_DATE}`]: { $exists: true }
      },
      {
        $or: [
          { signedAttachmentId: { $ne: null } },
          { [`values.${DocumentValueCodes.DELIVERY_DATE}`]: { $exists: true } }
        ]
      },
      ...filters.filter(filter => filter && Object.values(filter).filter(value => !isNullable(value)).length)
    ]
  };
}

/**
 * Zwraca listę pól po których ma nastąpić rozwinięcie tablicy wyników
 *
 * @export
 * @param {Route} route
 * @returns {string[]}
 */
export function getReportUnwinds(route: Route): string[] {
  const procedureId = route.params.procedureId.toString();
  const storageColumnsJSON = sessionStorage.getItem(Storage.REPORT_COLUMNS + procedureId);
  const storageColumns: any[] = parseJsonOrDefault<any[]>(storageColumnsJSON, []);

  if (!storageColumns.length) {
    return [];
  }

  const allUnwinds = storageColumns
    .map(column => column.unwind)
    .filter(Boolean)
    .reduce<string[]>((accumulator, currentUnwinds) => accumulator.concat(currentUnwinds), []);
  return allUnwinds.filter((unwind, index) => allUnwinds.indexOf(unwind) === index).sort((a, b) => a.length - b.length);
}
